import { useState, useEffect } from 'react';
import {
  DownloadIcon,
  CircleNotchIcon,
  WarningCircleIcon,
  ArrowsOutIcon,
  ArrowsOutLineHorizontalIcon,
  ArrowsInLineHorizontalIcon,
} from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { VirtualizedTable } from './VirtualizedTable';
import { useXlsxParser } from './use-xlsx-parser';
import { XlsxParseErrorText } from './xlsxParseErrorText';
import { useShownSheet } from './useShownSheet';
import type { SheetData, XlsxViewerProps } from './types';
import { T, Var, Num, Plural, useGT } from 'gt-react';
import { FullscreenOverlay } from '@/components/common/FullscreenOverlay';
import { ZoomControls } from '@/components/common/ZoomControls';
import { useZoom, type ZoomState } from '@/components/common/useZoom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const FULLSCREEN_HEADER_HEIGHT = 56;
const SHEET_TABS_HEIGHT = 40;

function RowCount({ count }: { count: number }) {
  return (
    <T context="spreadsheet row count label">
      <Var>{count}</Var> <Plural n={count} one="row" other="rows" />
    </T>
  );
}

/** Toggles between fit-to-card columns and columns sized to their content. */
function ColumnWidthToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const gt = useGT();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          aria-pressed={expanded}
          aria-label={gt('Expand columns to content', {
            context: 'xlsx column width tooltip',
          })}
          onClick={onToggle}
        >
          {expanded ? (
            <ArrowsInLineHorizontalIcon className="h-4 w-4" />
          ) : (
            <ArrowsOutLineHorizontalIcon className="h-4 w-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {expanded ? (
          <T context="xlsx column width tooltip">Fit columns</T>
        ) : (
          <T context="xlsx column width tooltip">Expand columns to content</T>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function DownloadLink({
  url,
  fileName,
  variant = 'ghost',
  withLabel = true,
}: {
  url: string;
  fileName: string;
  variant?: 'ghost' | 'outline';
  withLabel?: boolean;
}) {
  return (
    <Button variant={variant} size="sm" className="h-8 px-2" asChild>
      <a href={url} download={fileName}>
        <DownloadIcon className="h-4 w-4 mr-1.5" />
        {withLabel && (
          <span className="text-sm">
            <T context="download spreadsheet file">Download</T>
          </span>
        )}
      </a>
    </Button>
  );
}

/** A sheet's data rows: all of them, even those a truncated preview left out. */
function sheetRowCount(sheet: SheetData): number {
  return sheet.truncated ? sheet.totalRows : sheet.rows.length;
}

/** Says a preview holds only the first rows of a sheet too large to parse whole. */
function TruncatedNotice({ sheet }: { sheet: SheetData }) {
  if (!sheet.truncated) return null;
  return (
    <p
      role="status"
      className="border-b bg-muted/40 px-3 py-1 text-xs text-muted-foreground"
    >
      <T context="spreadsheet preview truncated notice">
        Showing the first <Num>{sheet.rows.length}</Num> of{' '}
        <Num>{sheet.totalRows}</Num> rows. Download the file to see every row.
      </T>
    </p>
  );
}

function SheetSummary({ name, rowCount }: { name: string; rowCount: number }) {
  return (
    <div className="flex items-center h-10 px-3 gap-2">
      <span className="text-sm font-medium">{name}</span>
      <span className="text-xs text-muted-foreground">
        (<RowCount count={rowCount} />)
      </span>
    </div>
  );
}

export function SheetTabs({
  sheets,
  activeSheetName,
  onSelect,
}: {
  sheets: SheetData[];
  activeSheetName: string;
  onSelect: (name: string) => void;
}) {
  const gt = useGT();
  // The @erp/ui Tabs: a tablist whose arrow keys move between sheets, as a
  // spreadsheet's sheet tabs do. The sheet itself renders above, not as a panel.
  return (
    <Tabs value={activeSheetName} onValueChange={onSelect} className="gap-0">
      <TabsList
        aria-label={gt('Sheets', { context: 'xlsx viewer sheet tabs' })}
        className="h-10 items-end gap-1 border-b-0 bg-transparent px-2"
      >
        {sheets.map((sheet) => (
          <Tooltip key={sheet.name}>
            <TooltipTrigger asChild>
              <TabsTrigger
                value={sheet.name}
                className={cn(
                  'h-8 rounded-t-md border border-b-0 px-3 text-sm',
                  'data-[active]:mb-0 data-[active]:border-border data-[active]:bg-background data-[active]:font-semibold data-[active]:text-foreground',
                  'border-border/60 bg-muted/70 text-foreground/70 hover:bg-muted/80 hover:text-foreground',
                )}
              >
                {sheet.name}
              </TabsTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <RowCount count={sheetRowCount(sheet)} />
            </TooltipContent>
          </Tooltip>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** Non-preview states (loading / parse error / empty workbook) share a box. */
function XlsxPlaceholder({
  height,
  children,
}: {
  height: string | number;
  children: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 border rounded-md bg-muted/10"
      style={{ height }}
    >
      {children}
    </div>
  );
}

function XlsxErrorView({
  height,
  error,
  url,
  fileName,
}: {
  height: string | number;
  error: string;
  url: string;
  fileName: string;
}) {
  return (
    <XlsxPlaceholder height={height}>
      <div className="flex items-center gap-2 text-destructive">
        <WarningCircleIcon className="h-5 w-5" />
        <span className="text-sm">
          <XlsxParseErrorText error={error} />
        </span>
      </div>
      <DownloadLink url={url} fileName={fileName} variant="outline" />
    </XlsxPlaceholder>
  );
}

function useViewportHeight() {
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === 'undefined' ? 800 : window.innerHeight,
  );

  useEffect(() => {
    function handleResize() {
      setViewportHeight(window.innerHeight);
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewportHeight;
}

/**
 * The spreadsheet taking over the viewport. Lives apart from the inline card
 * so neither branch has to carry the other's layout.
 */
function XlsxFullscreenView({
  sheet,
  sheetTabs,
  hasMultipleSheets,
  fileName,
  url,
  expanded,
  onToggleExpanded,
  zoomState,
  onClose,
}: {
  sheet: SheetData;
  sheetTabs: React.ReactNode;
  hasMultipleSheets: boolean;
  fileName: string;
  url: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  zoomState: ZoomState;
  onClose: () => void;
}) {
  const viewportHeight = useViewportHeight();
  const tableHeight = Math.max(
    200,
    Math.floor(
      viewportHeight -
        FULLSCREEN_HEADER_HEIGHT -
        (hasMultipleSheets ? SHEET_TABS_HEIGHT : 0),
    ),
  );

  return (
    <FullscreenOverlay
      title={fileName}
      onClose={onClose}
      actions={
        <>
          <ZoomControls state={zoomState} />
          <ColumnWidthToggle expanded={expanded} onToggle={onToggleExpanded} />
          <DownloadLink url={url} fileName={fileName} variant="outline" />
        </>
      }
    >
      <TruncatedNotice sheet={sheet} />
      <div className="flex-1 min-h-0 overflow-hidden">
        <VirtualizedTable
          sheet={sheet}
          height={tableHeight}
          expanded={expanded}
          zoom={zoomState.zoom}
        />
      </div>
      {sheetTabs && <div className="border-t bg-muted/50">{sheetTabs}</div>}
    </FullscreenOverlay>
  );
}

/** The spreadsheet inline, filling whatever definite-height box it's given. */
function XlsxCardView({
  sheet,
  sheetName,
  sheetTabs,
  fileName,
  url,
  height,
  expanded,
  onToggleExpanded,
  zoomState,
  showZoomControls,
  onEnterFullscreen,
}: {
  sheet: SheetData;
  sheetName: string;
  sheetTabs: React.ReactNode;
  fileName: string;
  url: string;
  height: string | number;
  expanded: boolean;
  onToggleExpanded: () => void;
  zoomState: ZoomState;
  /** False when the host page renders the zoom controls in its own toolbar. */
  showZoomControls: boolean;
  onEnterFullscreen: () => void;
}) {
  return (
    <div
      className="flex flex-col border rounded-md overflow-hidden bg-background"
      style={{ height }}
    >
      <div className="flex items-center justify-between border-b bg-muted/30 px-2">
        <SheetSummary name={sheetName} rowCount={sheetRowCount(sheet)} />
        <div className="flex items-center gap-1">
          {showZoomControls && <ZoomControls state={zoomState} />}
          <ColumnWidthToggle expanded={expanded} onToggle={onToggleExpanded} />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={onEnterFullscreen}
              >
                <ArrowsOutIcon className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <T context="xlsx fullscreen tooltip">Fullscreen</T>
            </TooltipContent>
          </Tooltip>
          <DownloadLink url={url} fileName={fileName} />
        </div>
      </div>
      <TruncatedNotice sheet={sheet} />
      <div className="flex-1 min-h-0 overflow-hidden">
        <VirtualizedTable
          sheet={sheet}
          height="100%"
          expanded={expanded}
          zoom={zoomState.zoom}
        />
      </div>
      {sheetTabs && <div className="border-t bg-muted/50">{sheetTabs}</div>}
    </div>
  );
}

export function XlsxViewer({
  url,
  fileName,
  mimeType,
  previewPlainTextAsTable,
  height = 450,
  zoom,
  sheetName,
  onSheetChange,
}: XlsxViewerProps) {
  const { sheets, isLoading, error } = useXlsxParser(
    url,
    fileName,
    mimeType,
    previewPlainTextAsTable,
  );
  const { currentSheet, selectSheet } = useShownSheet(
    sheets,
    sheetName,
    onSheetChange,
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ownZoom = useZoom();
  const zoomState = zoom ?? ownZoom;

  if (isLoading) {
    return (
      <XlsxPlaceholder height={height}>
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </XlsxPlaceholder>
    );
  }

  if (error) {
    return (
      <XlsxErrorView
        height={height}
        error={error}
        url={url}
        fileName={fileName}
      />
    );
  }

  if (!currentSheet) {
    return (
      <XlsxPlaceholder height={height}>
        <span className="text-sm text-muted-foreground">
          <T context="empty spreadsheet file message">
            This spreadsheet appears to be empty
          </T>
        </span>
        <DownloadLink url={url} fileName={fileName} variant="outline" />
      </XlsxPlaceholder>
    );
  }

  const hasMultipleSheets = sheets.length > 1;
  const sheetTabs = hasMultipleSheets ? (
    <SheetTabs
      sheets={sheets}
      activeSheetName={currentSheet.name}
      onSelect={selectSheet}
    />
  ) : null;
  const toggleExpanded = () => setExpanded(!expanded);

  if (isFullscreen) {
    return (
      <XlsxFullscreenView
        sheet={currentSheet}
        sheetTabs={sheetTabs}
        hasMultipleSheets={hasMultipleSheets}
        fileName={fileName}
        url={url}
        expanded={expanded}
        onToggleExpanded={toggleExpanded}
        zoomState={zoomState}
        onClose={() => setIsFullscreen(false)}
      />
    );
  }

  return (
    <XlsxCardView
      sheet={currentSheet}
      sheetName={currentSheet.name}
      sheetTabs={sheetTabs}
      fileName={fileName}
      url={url}
      height={height}
      expanded={expanded}
      onToggleExpanded={toggleExpanded}
      zoomState={zoomState}
      showZoomControls={zoom === undefined}
      onEnterFullscreen={() => setIsFullscreen(true)}
    />
  );
}
